/**
 * EnsoMirai++ sidebar — platform view, and a project view with its phases.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Drawer, useMediaQuery, useTheme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  orgNavigation,
  getProjectNavigation,
  isNavigationItemActive,
  type NavigationItem,
} from '../../navigation/navigationConfig';
import { useAppStore } from '../../store/appStore';
import { useAmsInbox, useProjects } from '../../services/api';
import { BrandLockup } from '../common/BrandLockup';
import { CLOSED_INCIDENT } from '../../lib/incidentStatus';
import { useSeenIncidents } from '../../lib/seenIncidents';
import { SidebarAccountBar } from './SidebarAccountBar';
import { SideTip } from './SideTip';
import { useProjectLifecycle, type ProjectLifecycle } from '../projects/workspace/useProjectLifecycle';
import { prefetchRouteCode } from '../../routes/routeLoaders';
import './sidebar.css';

const DRAWER_WIDTH = 264;
const COLLAPSED_WIDTH = 76;

export const ICONS: Record<string, React.ReactNode> = {
  overview: (
    <>
      <rect x="3.5" y="3.5" width="7" height="9" rx="1.8" />
      <rect x="13.5" y="3.5" width="7" height="5" rx="1.8" />
      <rect x="13.5" y="11.5" width="7" height="9" rx="1.8" />
      <rect x="3.5" y="15.5" width="7" height="5" rx="1.8" />
    </>
  ),
  projects: (
    <>
      <path d="m12 3 8 4.5v9L12 21l-8-4.5v-9Z" />
      <path d="m4 7.5 8 4.5 8-4.5M12 12v9" />
    </>
  ),
  monitoring: (
    <>
      <rect x="3" y="4" width="18" height="13" rx="2.5" />
      <path d="M6.5 11h2.5l1.5-3 3 6 1.5-3h2.5" />
      <path d="M9 20.5h6" />
    </>
  ),
  settings: (
    <>
      <path d="M4 7h9M17 7h3M4 17h3M11 17h9" />
      <circle cx="15" cy="7" r="2.2" />
      <circle cx="9" cy="17" r="2.2" />
    </>
  ),
  discover: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m15.3 15.3 5 5" />
      <path d="M9 8.3 6.9 10.5 9 12.7M12 8.3l2.1 2.2-2.1 2.2" />
    </>
  ),
  understand: (
    <>
      <path d="M9.5 4a3 3 0 0 0-3 3 3 3 0 0 0-2 5 3 3 0 0 0 2 5 3 3 0 0 0 3 3A2.5 2.5 0 0 0 12 17.5v-11A2.5 2.5 0 0 0 9.5 4Z" />
      <path d="M14.5 4a3 3 0 0 1 3 3 3 3 0 0 1 2 5 3 3 0 0 1-2 5 3 3 0 0 1-3 3 2.5 2.5 0 0 1-2.5-2.5v-11A2.5 2.5 0 0 1 14.5 4Z" />
    </>
  ),
  operate: (
    <>
      <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
      <path d="m7 10 2.5 2.5L7 15" />
      <path d="M12 15h4.5" />
    </>
  ),
};

const Icon: React.FC<{ name: string }> = ({ name }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    {ICONS[name] ?? ICONS.overview}
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </svg>
);

/** Lifecycle phases shown in the project view, in order (1-based "Phase N of 3"). */
const PHASES = [
  { id: 'discover', label: 'Discover', cls: 'ph-d', access: '0' },
  { id: 'understand', label: 'Understand', cls: 'ph-u', access: '1' },
  { id: 'operate', label: 'Operate', cls: 'ph-o', access: '3' },
] as const;

const PHASE_COLOR = ['var(--sd-discover)', 'var(--sd-understand)', 'var(--sd-operate)'];

/** 1-based index of the project's current phase. */
function currentPhaseNumber(life: ProjectLifecycle): number {
  const current =
    life.currentPhase ??
    (!life.hasIngestedFiles ? 'discover' : life.understandingCompleted ? 'operate' : 'understand');
  if (current === 'discover') return 1;
  if (current === 'understand') return 2;
  return 3;
}

/** Dot + coloured phase name, then "Phase N of M" or (compact) step bars. */
const PhaseMeta: React.FC<{ n: number; compact?: boolean }> = ({ n, compact }) => (
  <span className="proj-ph" style={{ '--ph-c': PHASE_COLOR[n - 1] } as React.CSSProperties}>
    <i className="dot" />
    <b>{PHASES[n - 1].label}</b>
    {compact ? (
      <span className="steps" aria-label={`Phase ${n} of ${PHASES.length}`}>
        {PHASES.map((ph, i) => (
          <i key={ph.id} className={i < n ? 'on' : undefined} />
        ))}
      </span>
    ) : (
      <span>
        Phase {n} of {PHASES.length}
      </span>
    )}
  </span>
);

function projectMark(name: string): string {
  const words = name.split(/[\s_-]+/).filter(Boolean);
  const mark = words.length > 1 ? words[0][0] + words[1][0] : name.slice(0, 2);
  return mark.toUpperCase();
}

/** Open incidents the user has not opened yet, per project, from the AMS inbox. */
function useOpenIncidentsByProject(): Map<string, number> {
  const { data: inbox = [] } = useAmsInbox();
  const seen = useSeenIncidents();
  return useMemo(() => {
    const out = new Map<string, number>();
    for (const item of inbox) {
      if (CLOSED_INCIDENT.has(item.status?.toLowerCase() ?? '') || seen.has(item.id)) continue;
      out.set(item.project_id, (out.get(item.project_id) ?? 0) + 1);
    }
    return out;
  }, [inbox, seen]);
}

interface NavLinkProps {
  item: NavigationItem;
  active: boolean;
  rail: boolean;
  tip: string;
  className?: string;
  disabled?: boolean;
  trailing?: React.ReactNode;
  onGo: (path: string) => void;
}

const NavLink: React.FC<NavLinkProps> = ({ item, active, rail, tip, className, disabled, trailing, onGo }) => (
  <li>
    <SideTip title={tip} show={rail}>
      <a
        href={item.path}
        className={className}
        aria-current={active ? 'page' : undefined}
        aria-disabled={disabled || undefined}
        // Code only — never API data — so hovering the menu stays cheap.
        onMouseEnter={() => prefetchRouteCode(item.path)}
        onFocus={() => prefetchRouteCode(item.path)}
        onClick={(e) => {
          e.preventDefault();
          if (!disabled) onGo(item.path);
        }}
      >
        <Icon name={item.icon} />
        <span className="lbl">{item.label}</span>
        {trailing}
      </a>
    </SideTip>
  </li>
);

/** Groups items under their `section` heading, keeping the original order. */
function groupBySection(items: NavigationItem[]): Array<{ section?: string; items: NavigationItem[] }> {
  const groups: Array<{ section?: string; items: NavigationItem[] }> = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (last && last.section === item.section) last.items.push(item);
    else groups.push({ section: item.section, items: [item] });
  }
  return groups;
}

const SectionLabel: React.FC<{ label?: string }> = ({ label }) =>
  label ? (
    <div className="nav-k">
      <span>{label}</span>
    </div>
  ) : null;

const PlatformView: React.FC<{
  rail: boolean;
  pathname: string;
  projectCount: number;
  onGo: (path: string) => void;
}> = ({ rail, pathname, projectCount, onGo }) => (
  <div className="view">
    {groupBySection(orgNavigation).map((group) => (
      <React.Fragment key={group.section ?? group.items[0].id}>
        <SectionLabel label={group.section} />
        <ul className="nav">
          {group.items.map((item) => {
            const count = item.id === 'projects' ? projectCount : item.badge ?? 0;
            return (
              <NavLink
                key={item.id}
                item={item}
                rail={rail}
                tip={count > 0 ? `${item.label} · ${count}` : item.label}
                active={isNavigationItemActive(item, pathname)}
                trailing={count > 0 ? <span className="badge">{count}</span> : null}
                onGo={onGo}
              />
            );
          })}
        </ul>
      </React.Fragment>
    ))}
  </div>
);

const ProjectMenuRow: React.FC<{
  id: string;
  name: string;
  selected: boolean;
  onPick: (id: string) => void;
}> = ({ id, name, selected, onPick }) => {
  const life = useProjectLifecycle(id);
  return (
    <button type="button" role="option" aria-selected={selected} onClick={() => onPick(id)}>
      <span className="pm">{projectMark(name)}</span>
      <span className="proj-txt">
        <b className="pm-n">{name}</b>
        <PhaseMeta n={currentPhaseNumber(life)} compact />
      </span>
      <CheckIcon />
    </button>
  );
};

const ProjectView: React.FC<{
  rail: boolean;
  pathname: string;
  projectId: string;
  projects: Array<{ id: string; name: string }>;
  openIncidents: Map<string, number>;
  onGo: (path: string) => void;
}> = ({ rail, pathname, projectId, projects, openIncidents, onGo }) => {
  const life = useProjectLifecycle(projectId);
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const name = projects.find((p) => p.id === projectId)?.name ?? projectId;
  const current = currentPhaseNumber(life);
  const incidents = openIncidents.get(projectId) ?? 0;
  const [home, ...phaseItems] = getProjectNavigation(projectId);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  useEffect(() => setMenuOpen(false), [projectId]);

  return (
    <div className="view project">
      <SideTip title="All projects" show={rail}>
        <button type="button" className="back" onClick={() => onGo('/projects/all')}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M19 12H5M11 6l-6 6 6 6" />
          </svg>
          <span className="lbl">All projects</span>
        </button>
      </SideTip>

      <div className="proj-wrap" ref={wrapRef}>
        <SideTip title={name} show={rail && !menuOpen}>
          <button
            type="button"
            className="proj"
            style={{ '--ph-c': PHASE_COLOR[current - 1] } as React.CSSProperties}
            aria-haspopup="listbox"
            aria-expanded={menuOpen}
            aria-label={`Switch project, current ${name}`}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span className="proj-mark">{projectMark(name)}</span>
            <span className="proj-txt">
              <b>{name}</b>
              <PhaseMeta n={current} />
            </span>
            <span className="chev" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="m8 9 4-4 4 4M8 15l4 4 4-4" />
              </svg>
            </span>
          </button>
        </SideTip>
        {menuOpen && (
          <div className="proj-menu" role="listbox" aria-label="Switch project">
            <div className="proj-menu-lbl">Projects</div>
            {projects.map((p) => (
              <ProjectMenuRow
                key={p.id}
                id={p.id}
                name={p.name}
                selected={p.id === projectId}
                onPick={(id) => {
                  setMenuOpen(false);
                  onGo(`/projects/${id}`);
                }}
              />
            ))}
          </div>
        )}
      </div>

      <ul className="nav" style={{ marginTop: 14 }}>
        <NavLink
          item={home}
          rail={rail}
          tip={home.label}
          active={isNavigationItemActive(home, pathname)}
          onGo={onGo}
        />
      </ul>

      <SectionLabel label={phaseItems[0]?.section} />
      <ul className="nav">
        {phaseItems.map((item) => {
          const idx = PHASES.findIndex((ph) => ph.id === item.id);
          const phase = PHASES[idx];
          if (!phase) return null;
          const n = idx + 1;
          const locked = life.phaseAccess[phase.access] === 'locked';
          const badge = phase.id === 'operate' && incidents > 0 ? incidents : 0;
          const status = locked ? (
            <span className="ph-st lock" title={`Finish ${PHASES[idx - 1]?.label ?? 'the previous phase'} to unlock`}>
              <LockIcon />
            </span>
          ) : n === current ? (
            <span className="ph-st now">
              <i />
              Now
            </span>
          ) : n < current ? (
            <span className="ph-st done" title="Complete">
              <CheckIcon />
            </span>
          ) : null;
          const tip =
            item.label +
            (locked ? ' · locked' : n === current ? ' · current phase' : '') +
            (badge ? ` · ${badge} open incident${badge === 1 ? '' : 's'}` : '');
          return (
            <NavLink
              key={item.id}
              item={item}
              rail={rail}
              tip={tip}
              className={`ph ${phase.cls}`}
              disabled={locked}
              active={isNavigationItemActive(item, pathname)}
              trailing={
                badge ? (
                  <span className="badge crit" title={`${badge} open incident${badge === 1 ? '' : 's'}`}>
                    {badge}
                  </span>
                ) : (
                  status
                )
              }
              onGo={onGo}
            />
          );
        })}
      </ul>
    </div>
  );
};

export const Sidebar: React.FC<{ open: boolean; onToggle: () => void }> = ({ open, onToggle }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarCollapsed: collapsed, setSidebarCollapsed } = useAppStore();
  const { projects } = useProjects();
  const openIncidents = useOpenIncidentsByProject();
  const rail = collapsed && isDesktop;

  const workspaceProjectId = useMemo(() => {
    const match = location.pathname.match(/^\/projects\/([^/]+)/);
    if (match && !['all', 'active', 'templates'].includes(match[1])) {
      return match[1];
    }
    return null;
  }, [location.pathname]);

  const go = (path: string) => {
    navigate(path);
    if (!isDesktop) onToggle();
  };

  const drawerContent = (
    <aside className={`ens-side${rail ? ' collapsed' : ''}`} aria-label="Navigation">
      <div className="brand">
        <div className="brand-txt">
          <BrandLockup height={28} compact={rail} />
        </div>
        {isDesktop && (
          <button
            type="button"
            className="collapse-btn"
            aria-label={rail ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!rail}
            onClick={(e) => {
              e.stopPropagation();
              setSidebarCollapsed(!collapsed);
            }}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
              <path d="M9.5 4.5v15M15.5 10l-2 2 2 2" />
            </svg>
          </button>
        )}
      </div>

      {workspaceProjectId ? (
        <ProjectView
          key="project"
          rail={rail}
          pathname={location.pathname}
          projectId={workspaceProjectId}
          projects={projects}
          openIncidents={openIncidents}
          onGo={go}
        />
      ) : (
        <PlatformView
          key="platform"
          rail={rail}
          pathname={location.pathname}
          projectCount={projects.length}
          onGo={go}
        />
      )}

      <div className="grow" />

      <div className="foot">
        <SidebarAccountBar collapsed={rail} />
      </div>
    </aside>
  );

  const drawerPaperSx = {
    width: rail ? COLLAPSED_WIDTH : DRAWER_WIDTH,
    boxSizing: 'border-box' as const,
    borderRight: 'none',
    backgroundImage: 'none',
  };

  return (
    <>
      <Drawer
        variant="temporary"
        open={!isDesktop && open}
        onClose={onToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': drawerPaperSx,
        }}
        PaperProps={{ className: 'ens-drawer' }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', lg: 'block' },
          width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            ...drawerPaperSx,
            width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
            transition: theme.transitions.create('width', {
              easing: 'cubic-bezier(.3, .8, .3, 1)',
              duration: 250,
            }),
          },
        }}
        PaperProps={{ className: 'ens-drawer' }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
